import { useEffect, useMemo, useState } from 'react';
import type { WorkshopDto } from '@workshop-hst/shared';

type WorkflowStepId = 'kickoff' | 'format-check' | 'logistics' | 'thank-you';

type HostCrmState = {
  completed: WorkflowStepId[];
};

const STEP_LABELS: Record<WorkflowStepId, string> = {
  kickoff: 'Host kickoff',
  'format-check': 'Format check-in',
  logistics: 'Host logistics',
  'thank-you': 'Thank-you',
};

function crmStorageKey(workshopId: string): string {
  return `workshop-hst:host-crm:${workshopId}`;
}

function addBusinessDays(date: Date, days: number): Date {
  const next = new Date(date);
  let remaining = days;
  const direction = days >= 0 ? 1 : -1;
  while (remaining !== 0) {
    next.setDate(next.getDate() + direction);
    const day = next.getDay();
    if (day !== 0 && day !== 6) remaining -= direction;
  }
  return next;
}

function formatDateOnly(isoOrDate: string | Date): string {
  const d =
    typeof isoOrDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(isoOrDate)
      ? new Date(`${isoOrDate}T12:00:00`)
      : new Date(isoOrDate);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function timeWithZone(iso: string): string {
  return formatTime(new Date(iso), true);
}

function formatTime(date: Date, includeZone = false): string {
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    ...(includeZone ? { timeZoneName: 'short' as const } : {}),
  });
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}

function computedSegmentMin(durationMin: number): number {
  return Math.max(15, Math.min(45, durationMin - 15));
}

function defaultState(workshop: WorkshopDto): HostCrmState {
  return {
    completed: [],
  };
}

function loadState(workshop: WorkshopDto): HostCrmState {
  const fallback = defaultState(workshop);
  try {
    const raw = localStorage.getItem(crmStorageKey(workshop.id));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<HostCrmState>;
    return {
      ...fallback,
      ...parsed,
      completed: Array.isArray(parsed.completed) ? parsed.completed : [],
    };
  } catch {
    return fallback;
  }
}

function publicPortalLink(workshop: WorkshopDto): string {
  if (typeof window === 'undefined') return `/w/${workshop.slug}`;
  return `${window.location.origin}/w/${workshop.slug}`;
}

function copyText(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

function mailto(email: string | null | undefined, subject: string, body: string): string {
  const to = email ?? '';
  return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function HostCrmPanel({ workshop }: { workshop: WorkshopDto }) {
  const [state, setState] = useState<HostCrmState>(() => loadState(workshop));
  const [active, setActive] = useState<WorkflowStepId>('kickoff');
  const [copied, setCopied] = useState<WorkflowStepId | null>(null);

  useEffect(() => {
    setState(loadState(workshop));
    setActive('kickoff');
  }, [workshop.id]);

  useEffect(() => {
    localStorage.setItem(crmStorageKey(workshop.id), JSON.stringify(state));
  }, [state, workshop.id]);

  const templates = useMemo(() => buildTemplates(workshop), [workshop]);
  const activeTemplate = templates[active];
  const completedSet = new Set(state.completed);
  const guestMissing = !workshop.guest?.email;

  const toggleCompleted = (id: WorkflowStepId) => {
    setState((current) => {
      const has = current.completed.includes(id);
      return {
        ...current,
        completed: has ? current.completed.filter((x) => x !== id) : [...current.completed, id],
      };
    });
  };

  return (
    <section className="rounded-2xl border border-line bg-surface p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Host CRM</h2>
          <p className="mt-1 text-xs text-ink-muted">
            Sequential outreach for this workshop. Progress and fields are saved in this browser.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="chip-pill">{state.completed.length} / 4 done</span>
          {workshop.guest?.email ? (
            <span className="chip-pill normal-case">{workshop.guest.email}</span>
          ) : (
            <span className="chip-pill border-red-200 bg-red-50 text-red-700">Guest email missing</span>
          )}
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[240px_1fr]">
        <div className="space-y-2">
          {(Object.keys(STEP_LABELS) as WorkflowStepId[]).map((id, index) => (
            <button
              key={id}
              type="button"
              onClick={() => setActive(id)}
              className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                active === id
                  ? 'border-ink bg-ink text-white'
                  : 'border-line bg-canvas/40 text-ink hover:bg-canvas'
              }`}
            >
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  active === id ? 'bg-white text-ink' : 'bg-surface text-ink-muted'
                }`}
              >
                {index + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{STEP_LABELS[id]}</span>
                <span className={`block text-xs ${active === id ? 'text-white/70' : 'text-ink-subtle'}`}>
                  {templates[id].timing}
                </span>
              </span>
              <input
                type="checkbox"
                checked={completedSet.has(id)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleCompleted(id);
                }}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
                aria-label={`Mark ${STEP_LABELS[id]} complete`}
              />
            </button>
          ))}
        </div>

        <div className="min-w-0">
          <div className="overflow-hidden rounded-xl border border-line">
            <div className="border-b border-line bg-canvas/60 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Subject</p>
              <p className="mt-1 text-sm font-medium">{activeTemplate.subject}</p>
            </div>
            <textarea
              className="min-h-80 w-full resize-y border-0 bg-surface p-4 font-mono text-xs leading-5 text-ink outline-none"
              readOnly
              value={activeTemplate.body}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => toggleCompleted(active)}
            >
              {completedSet.has(active) ? 'Mark not sent' : 'Mark sent'}
            </button>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  copyText(`Subject: ${activeTemplate.subject}\n\n${activeTemplate.body}`).then(() => {
                    setCopied(active);
                    window.setTimeout(() => setCopied(null), 1500);
                  });
                }}
              >
                {copied === active ? 'Copied' : 'Copy email'}
              </button>
              <a
                className={`btn-accent ${guestMissing ? 'pointer-events-none opacity-50' : ''}`}
                href={mailto(workshop.guest?.email, activeTemplate.subject, activeTemplate.body)}
                aria-disabled={guestMissing}
              >
                Open email
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function buildTemplates(workshop: WorkshopDto): Record<WorkflowStepId, { subject: string; body: string; timing: string }> {
  const guestName = workshop.guest?.name ?? '[Name]';
  const company = workshop.guest?.companyName ?? '[Company]';
  const guestIntro = workshop.guest
    ? `${workshop.guest.name} from ${company} will walk through how they approach this in practice.`
    : '[one-sentence intro, confirm or edit]';
  const workshopDate = formatDateOnly(workshop.startsAt);
  const workshopTime = timeWithZone(workshop.startsAt);
  const start = new Date(workshop.startsAt);
  const introTime = formatTime(start);
  const segmentTime = formatTime(addMinutes(start, 5));
  const segmentMinutes = computedSegmentMin(workshop.durationMin);
  const qnaOffset = Math.min(workshop.durationMin - 10, 5 + segmentMinutes);
  const qnaTime = formatTime(addMinutes(start, Math.max(5, qnaOffset)));
  const wrapTime = formatTime(addMinutes(start, workshop.durationMin));
  const portalLink = publicPortalLink(workshop);
  const repoLink = '[repo link]';
  const length = `${workshop.durationMin} minutes`;
  const segment = `${segmentMinutes} minutes`;
  const audience = 'operators, founders, and builders who want practical examples they can reuse immediately';
  const replyDue = formatDateOnly(addBusinessDays(new Date(), 3));
  const prDue = formatDateOnly(addBusinessDays(start, -4));
  const signoff = 'Colin';
  const firstName = guestName.split(' ')[0] || guestName;
  const recordingLine = 'We will record the session. The recording goes to registered attendees afterward. Reply to confirm you are fine with that.';
  const logisticsFormatLine = `We will use the format you confirmed. If it is a worksheet, it should be merged and live at ${portalLink}. If it is a demo, you will share your screen. Join 10 minutes early so we can confirm audio and screen share.`;
  const recordingShare = workshop.recordingPlayUrl
    ? ` The recording is at ${workshop.recordingPlayUrl} if you want to share it internally.`
    : ' [If recorded: The recording is at [link] if you want to share it internally.]';
  const scoreLine =
    '\n\nAttendees rated the session [score] out of [scale], and [pull one quote from the survey if a good one exists].';

  return {
    kickoff: {
      subject: `You're hosting ${workshop.title} on ${workshopDate}, hold the time`,
      timing: 'Send when published',
      body: `Hi ${firstName},

You're confirmed as guest host for ${workshop.title} on ${workshopDate} at ${workshopTime}. A calendar invite is on its way, and registration is live at ${portalLink}.

The session runs ${length}. You'll present for roughly ${segment}, and we'll use the rest for Q&A with attendees. The audience is ${audience}.

You have two options for your segment:

Worksheet in the portal. You open a pull request against ${repoLink} with a worksheet attendees work through live. I review and merge it, so I need the PR by ${prDue}.
Live demo with screen share. You demo your own stuff from your machine. No prep deliverable on my end, though I recommend a 15-minute tech check the week before.

Reply with your pick by ${replyDue} and I'll send logistics from there. If your company requires comms or legal review for external presentations, start that process now since it tends to take longer than the prep itself.

${signoff}`,
    },
    'format-check': {
      subject: `Re: ${workshop.title} on ${workshopDate}, which format`,
      timing: 'Send after 3-4 days if no reply',
      body: `Hi ${firstName},

Quick nudge on the workshop on ${workshopDate}. I need your format pick, worksheet PR or live demo, by ${replyDue} to lock the run-of-show. One line back is plenty.

${signoff}`,
    },
    logistics: {
      subject: `Run-of-show for ${workshopDate}`,
      timing: 'Send 2-3 days before',
      body: `Hi ${firstName},

Here's how ${workshopDate} runs:

${introTime}: I open the session, cover housekeeping, and introduce you. Your intro will be: "${guestIntro}"
${segmentTime}: Your segment. ${logisticsFormatLine}
${qnaTime}: Q&A. I'll moderate and pull questions from chat.
${wrapTime}: Wrap.

${recordingLine}

If your demo or connection breaks mid-session, I'll pivot to Q&A while you recover, so nothing is lost.

Join link: ${workshop.zoomJoinUrl ?? '[link]'}

${signoff}`,
    },
    'thank-you': {
      subject: `Thank you for ${workshopDate}`,
      timing: 'Send within 24 hours after',
      body: `Hi ${firstName},

Thanks for hosting. [One specific thing that landed well.]${recordingShare}${scoreLine}

I run these weekly and the host pipeline is the hardest part to keep full. If a colleague at ${company} or elsewhere would be a fit, an intro would help me more than anything else.

${signoff}`,
    },
  };
}
